import { Button, Divider, Form, Input, InputNumber, Select, Table, Tag, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { Container, FiltersContainer, StatsContainer, StatCard } from './styles';

const { Option } = Select;

const conditionOptions = ['For Parts', 'New', 'Open Box', 'Used'];

const conditionColors = {
  'For Parts': 'red',
  New: 'green',
  'Open Box': 'blue',
  Used: 'orange',
};

export function AgedInventoryReport() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    loadCategories();
    loadSubcategories();
    // Set default values
    form.setFieldsValue({
      qty: 1,
      type: 'years',
      condition: [],
    });
  }, [form]);

  const loadCategories = async () => {
    try {
      const response = await api.get('categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      const response = await api.get('subcategories');
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const loadReport = async (page = 0, size = 25, filterValues = null, sort = null, order = null) => {
    try {
      setLoading(true);
      const values = filterValues || form.getFieldsValue();

      // Build request body
      const body = {
        qty: String(values.qty),
        type: values.type,
      };

      if (values.categoryId) {
        body.categoryId = values.categoryId;
      }

      if (values.subcategoryId) {
        body.subcategoryId = values.subcategoryId;
      }

      if (values.itemName) {
        body.itemName = values.itemName;
      }

      if (values.condition && values.condition.length > 0) {
        body.condition = values.condition;
      }

      // Build query params
      let queryParams = `page=${page}&size=${size}`;

      // Add sort parameters if they exist
      const currentSortField = sort !== null ? sort : sortField;
      const currentSortOrder = order !== null ? order : sortOrder;

      if (currentSortField && currentSortOrder) {
        queryParams += `&sortField=${currentSortField}&sortOrder=${currentSortOrder}`;
      }

      const response = await api.put(`/report/AgedInventoryReport?${queryParams}`, body);

      setData(response.data.rows);
      setPagination({
        current: response.data.currentPage,
        pageSize: response.data.sizePage,
        total: response.data.totalItems,
      });
      setFilters(response.data.filters);
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    // Reset sorting when generating a new report
    setSortField(null);
    setSortOrder(null);
    loadReport(0, pagination.pageSize, values, null, null);
  };

  const handleTableChange = (paginationConfig, filters, sorter) => {
    // Handle sorting
    let newSortField = null;
    let newSortOrder = null;

    if (sorter && sorter.field) {
      newSortField = sorter.field;
      newSortOrder = sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : null;

      setSortField(newSortField);
      setSortOrder(newSortOrder);
    } else {
      // Reset sorting
      setSortField(null);
      setSortOrder(null);
    }

    loadReport(paginationConfig.current - 1, paginationConfig.pageSize, null, newSortField, newSortOrder);
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({
      qty: 1,
      type: 'years',
      condition: [],
    });
    setData([]);
    setFilters(null);
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const values = form.getFieldsValue();

      // Build request body
      const body = {
        qty: String(values.qty),
        type: values.type,
      };

      if (values.categoryId) {
        body.categoryId = values.categoryId;
      }

      if (values.subcategoryId) {
        body.subcategoryId = values.subcategoryId;
      }

      if (values.itemName) {
        body.itemName = values.itemName;
      }

      if (values.condition && values.condition.length > 0) {
        body.condition = values.condition;
      }

      // Add sort parameters and export flag
      let queryParams = '?export=excel';
      if (sortField && sortOrder) {
        queryParams += `&sortField=${sortField}&sortOrder=${sortOrder}`;
      }

      const response = await api.put(`/report/AgedInventoryReport${queryParams}`, body, {
        responseType: 'blob',
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Aged_Inventory_Report_${moment().format('YYYY-MM-DD_HHmm')}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const calculateAge = (createdAt) => {
    const now = moment();
    const created = moment(createdAt);
    const diffDays = now.diff(created, 'days');
    const diffMonths = now.diff(created, 'months');
    const diffYears = now.diff(created, 'years');

    if (diffYears > 0) {
      return `${diffYears} year${diffYears > 1 ? 's' : ''}`;
    } else if (diffMonths > 0) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
    } else {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }
  };

  const columns = [
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      fixed: 'left',
      width: 150,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Item',
      dataIndex: ['item', 'name'],
      key: 'item_name',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            <div>{text}</div>
            <small style={{ color: '#888' }}>{record.item.part_number}</small>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Category',
      dataIndex: ['item', 'category', 'name'],
      key: 'category',
      width: 120,
      // filters: categories.map((cat) => ({ text: cat.name, value: cat.id })),
      // onFilter: (value, record) => record.item.category.id === value,
    },
    {
      title: 'Subcategory',
      dataIndex: ['item', 'subcategory', 'name'],
      key: 'subcategory',
      width: 120,
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      width: 100,
      render: (condition) => <Tag color={conditionColors[condition] || 'default'}>{condition}</Tag>,
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={status === 'InStock' ? 'green' : 'default'}>{status}</Tag>,
    },
    {
      title: 'Location',
      dataIndex: ['locations', 'name'],
      key: 'location',
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'User Location',
      dataIndex: ['usersLocation', 'name'],
      key: 'user_location',
      width: 120,
    },
    {
      title: 'Age',
      dataIndex: 'updated_at',
      key: 'age',
      width: 120,
      render: (updatedAt) => calculateAge(updatedAt),
      sorter: true,
      sortOrder: sortField === 'updated_at' && sortOrder ? (sortOrder === 'ASC' ? 'ascend' : 'descend') : null,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <Container>
      <h1>Aged Inventory Report</h1>
      <p style={{ color: '#666', marginBottom: 20 }}>View inventory items that have been in stock for a specified period</p>

      <FiltersContainer>
        <Form form={form} name="agedInventoryReport" onFinish={onFinish} layout="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <Form.Item name="qty" label="Quantity" rules={[{ required: true, message: 'Please enter quantity' }]}>
              <InputNumber min={1} placeholder="Enter quantity" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="type" label="Time Unit" rules={[{ required: true, message: 'Please select time unit' }]}>
              <Select placeholder="Select time unit">
                <Option value="days">Days</Option>
                <Option value="months">Months</Option>
                <Option value="years">Years</Option>
              </Select>
            </Form.Item>

            <Form.Item name="categoryId" label="Category">
              <Select placeholder="Select category (optional)" allowClear showSearch optionFilterProp="children">
                {categories.map((category) => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="subcategoryId" label="Subcategory">
              <Select placeholder="Select subcategory (optional)" allowClear showSearch optionFilterProp="children">
                {subcategories.map((subcategory) => (
                  <Option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="itemName" label="Item Name">
              <Input placeholder="Search by item name (optional)" allowClear />
            </Form.Item>

            <Form.Item name="condition" label="Condition">
              <Select mode="multiple" placeholder="Select conditions (optional)" allowClear>
                {conditionOptions.map((condition) => (
                  <Option key={condition} value={condition}>
                    {condition}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 8 }}>
              Generate Report
            </Button>
            <Button onClick={handleReset} style={{ marginRight: 8 }}>
              Reset Filters
            </Button>
            <Button type="default" icon={<DownloadOutlined />} onClick={handleDownloadExcel} loading={downloadingExcel} disabled={!filters}>
              Download Excel
            </Button>
          </Form.Item>
        </Form>
      </FiltersContainer>

      {filters && (
        <>
          <Divider />
          <StatsContainer>
            <StatCard>
              <div className="stat-value">{pagination.total}</div>
              <div className="stat-label">Total Items</div>
            </StatCard>
            <StatCard>
              <div className="stat-value">
                {filters.qty} {filters.type}
              </div>
              <div className="stat-label">Age Filter</div>
            </StatCard>
            {filters.categoryId && (
              <StatCard>
                <div className="stat-value">{categories.find((c) => c.id === filters.categoryId)?.name || 'N/A'}</div>
                <div className="stat-label">Category Filter</div>
              </StatCard>
            )}
            {filters.subcategoryId && (
              <StatCard>
                <div className="stat-value">{subcategories.find((s) => s.id === filters.subcategoryId)?.name || 'N/A'}</div>
                <div className="stat-label">Subcategory Filter</div>
              </StatCard>
            )}
            {filters.condition && filters.condition.length > 0 && (
              <StatCard>
                <div className="stat-value">{filters.condition.join(', ')}</div>
                <div className="stat-label">Condition Filter</div>
              </StatCard>
            )}
          </StatsContainer>
        </>
      )}

      <Divider />

      <Table
        size="small"
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          ...pagination,
          current: pagination.current + 1,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '25', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
      />
    </Container>
  );
}
